"use client";
import React, { useEffect, useMemo, useTransition, useState } from "react";
import { notFound, useParams, useSearchParams } from "next/navigation";
import { CategoryPage } from "./categoryPage";
import { ItemPage } from "./itemPage";
import { sortOptions } from "@/components/sortDropdown/sortOptions";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { getItemsInCategoryAction } from "@/app/actions";
import {
  CategoryPageSkeleton,
  ItemPageSkeleton,
} from "@/components/loadingSkeleton/loadingSkeleton";
import { ClientWrapperProps } from "@/dto/clientWrapper";
import { Category } from "@/dto/category";
import {
  getCategoryItemsFromCache,
  CategoryItemsData,
  setCategoryItemsInCache,
} from "@/utils/menuCacheUtils";

export const SlugContainer = ({
  pageType,
  itemTemplate,
  merchant,
  categories,
  itemDetails,
}: ClientWrapperProps) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const [category, setCategory] = useState<Category | null>(null);
  const [isTransitioning, startTransition] = useTransition();
  const [categoryItemsData, setCategoryItemsData] =
    useState<CategoryItemsData | null>(null);

  const slug = useMemo(
    () => decodeURIComponent(params.slug as string),
    [params.slug]
  );

  const currentCategory = useMemo(
    () =>
      categories?.find(
        (cat: Category) => cat.name === slug || cat.name === slug + " "
      ) || null,
    [categories, slug]
  );

  useEffect(() => {
    if (currentCategory) {
      setCategory(currentCategory);
    } else {
      setCategory(null);
      setCategoryItemsData(null);
    }
  }, [currentCategory]);

  useEffect(() => {
    if (pageType !== "category" || !merchant || !currentCategory) {
      return;
    }

    const defaultSort = sortOptions[0];
    const payload = {
      mid: merchant.id,
      categoryId: currentCategory.id,
      searchQuery: searchParams.get("query") || "",
      pageIndex: parseInt(searchParams.get("page") || "1"),
      sortOrderColumn:
        searchParams.get("sortOrderColumn") || defaultSort.columns,
      sortBy: searchParams.get("sortOrder") || defaultSort.order,
      limit: process.env.PAGE_LIMIT || "32",
    };
    const isDefaultView =
      payload.pageIndex === 1 &&
      payload.searchQuery === "" &&
      payload.sortOrderColumn === defaultSort.columns &&
      payload.sortBy === defaultSort.order;

    if (isDefaultView) {
      const cachedData = getCategoryItemsFromCache(currentCategory.id);
      if (cachedData) {
        setCategoryItemsData(cachedData);
        return;
      }
    }

    startTransition(async () => {
      const result = await getItemsInCategoryAction(null, payload);
      if (result.data) {
        setCategoryItemsData(result.data);
        if (isDefaultView) {
          setCategoryItemsInCache(currentCategory.id, result.data);
        }
      } else {
        setCategoryItemsData(null);
      }
    });
  }, [pageType, merchant, currentCategory, searchParams]);

  let slugContent;

  if (pageType === "category") {
    if (isTransitioning || !merchant || !categoryItemsData || !category) {
      slugContent = <CategoryPageSkeleton itemTemplate={itemTemplate} />;
    } else {
      slugContent = (
        <CategoryPage
          items={categoryItemsData.elements}
          currentPage={categoryItemsData.currentPage}
          totalPages={categoryItemsData.totalPages}
          totalItemCount={categoryItemsData.totalItemCount}
          category={category}
          mid={merchant.id}
          itemTemplate={itemTemplate}
          isRetailMerchant={merchant.isRetail}
          isSimpleStorefront={merchant.merchantMeta.selectedSetup === "Simple"}
          pageSize={process.env.PAGE_LIMIT || "32"}
        />
      );
    }
  } else if (pageType === "item") {
    if (isTransitioning || !merchant) {
      slugContent = <ItemPageSkeleton />;
    }
    if (!isTransitioning && merchant && itemDetails) {
      slugContent = (
        <ItemPage
          itemData={itemDetails}
          checkoutEnabled={merchant.isCheckoutEnabled}
          isOpen={isBusinessOpen(merchant)}
          merchantColor={
            merchant.merchantMeta?.colorPalette?.color ||
            cloverColorTheme.Main.primaryColor
          }
          merchant={merchant}
        />
      );
    }
    if (!itemDetails) {
      slugContent = notFound();
    }
  } else {
    slugContent = notFound();
  }

  return <>{slugContent}</>;
};
